import {
  Box,
  CardMedia,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  Select,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

// assets
import JobBoardFooter from '../components/JobBoardFooter'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ClearIcon from '@mui/icons-material/Clear'
import notFoundIcon from '../assets/Union.svg'

// components
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxInput from 'components/custom/AdxInput'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import MobileJobBoardFilterDrawer from '../components/MobileJobBoardFilterDrawer'
import JobBoardMobileAccordion from '../components/JobBoardMobileAccordion'

// api
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useDebouncedEffect } from 'services/hooks/useDebouncedEffect'
import { getAllJobBoard, getBackground, getCity } from 'services/api/job-board'
import { getCompanyDetails } from 'services/api/company-details'
import AdxButton from 'components/custom/AdxButton'

const JobBoard = () => {
  const navigate = useNavigate()
  const params = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const company_uuid = params?.uuid
  const company_name = params?.companyName

  const { data: companyDetails, isLoading: companyDetailsIsLoading } = useQuery(
    'companyDetails',
    getCompanyDetails
  )

  const jobBoardStatus = companyDetails?.job_board

  const JOB_BOARD_IS_ACTIVE = 1
  const JOB_BOARD_IS_INACTIVE = 0

  // search
  const [searchItem, setSearchItem] = useState('')
  const [search, setSearch] = useState('')
  const handleSearch = (value) => {
    setSearchItem(value)
  }
  const handleClearSearch = () => {
    setSearchItem('')
  }
  // set delay for search api
  useDebouncedEffect(
    () => {
      if (searchItem.length > 0) {
        setSearch(`&position_title=${searchItem}`)
      } else {
        setSearch('')
      }
    },
    [searchItem],
    700
  )

  // API calls
  const { isLoading: backgroundIsLoading, data: backgroundData } = useQuery(
    ['getBackground', company_uuid],
    async () => {
      const { data } = await getBackground(company_uuid)

      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: cityIsLoading, data: cityData } = useQuery(
    ['getCity', company_uuid],
    async () => {
      const { data } = await getCity(company_uuid)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const cityDataArray = cityData
    ? Object.keys(cityData).map((key) => ({
        code: parseInt(key),
        value: cityData[key],
      }))
    : []

  // city filter
  const [cityFilter, setCityFilter] = useState(null)
  const [cityApi, setCityApi] = useState('')
  const handleCityChange = (event) => {
    setCityFilter(event.target.value)
  }

  // background filter
  const [backgroundFilter, setBackgroundFilter] = useState(null)
  const [backgroundApi, setBackgroundApi] = useState('')
  const handleBackgroundChange = (event) => {
    setBackgroundFilter(event.target.value)
  }

  // position type filter
  const [typeFilter, setTypeFilter] = useState([
    { name: 'Full time', value: 'full-time', checked: true },
    { name: 'Part time', value: 'part-time', checked: true },
    { name: 'Contract', value: 'contract', checked: true },
  ])
  const [typeApi, setTypeApi] = useState([])

  const handleCheckboxChange = (index) => {
    setTypeFilter((prevTypeFilter) =>
      prevTypeFilter?.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item))
    )
  }

  const getCheckedValuesString = () => {
    const checkedValues = typeFilter.filter((item) => item.checked).map((item) => item.value)
    return checkedValues.join(',')
  }

  useEffect(() => {
    if (!isMobile)
      setTypeApi(`position_type=${getCheckedValuesString()}` ?? 'fulltime,part-time,contract')
  }, [typeFilter])

  const {
    isLoading: allJobBoardIsLoading,
    data: allJobBoardData,
    isError: allJobBoardisError,
  } = useQuery(
    [
      'getAllJobBoard',
      company_uuid,
      search,
      typeApi,
      backgroundApi,
      cityApi,
      cityIsLoading,
      backgroundIsLoading,
    ],
    async () => {
      if (!cityIsLoading && !backgroundIsLoading) {
        const { data } = await getAllJobBoard(company_uuid, search, typeApi, backgroundApi, cityApi)
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        console.log(res)
        setDrawerState(false)
      },
      onError: (err) => {
        console.log(err)
      },
    }
  )

  const totalJobs = () => {
    let sum = 0
    allJobBoardData?.jobs?.forEach((element) => {
      const jobs = element?.advertisements?.length
      sum += jobs
    })
    return sum
  }

  const [drawerState, setDrawerState] = useState(false)

  const handleDrawerState = (state) => {
    setDrawerState(state)
  }

  const handleSearchJobsButton = () => {
    setBackgroundApi(
      backgroundFilter ? `job_background=${backgroundFilter?.job_background_id}` : ''
    )
    setCityApi(cityFilter ? `city=${cityFilter}` : '')
    if (isMobile)
      setTypeApi(`position_type=${getCheckedValuesString()}` ?? 'fulltime,part-time,contract')
  }

  const handleViewJob = (id) => {
    navigate(`/job-board/${company_name}/${company_uuid}/${id}`)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        gap: '32px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: isMobile ? '284px' : null,
          paddingTop: '26.953125%',
          backgroundImage: `
      linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 70%),
      linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 70%),
      url(/bee-honeycomb.png)
    `,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <Typography
          fontFamily={'Oswald'}
          fontWeight={600}
          variant="h1"
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white.main',
            textAlign: 'center',
            width: '100%',
          }}
        >
          Explore exciting career opportunities with us.
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingX: isMobile ? '20px' : '116px',
          paddingBottom: isMobile ? '20px' : '116px',
          gap: '32px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box>
            {companyDetailsIsLoading ? (
              <Skeleton variant="rounded" width={'88px'} height={'88px'} />
            ) : (
              <CardMedia
                component="img"
                sx={{
                  width: isMobile ? '60px' : '88px',
                  height: isMobile ? '60px' : '88px',
                  objectFit: 'contain',
                  border: 1,
                  borderRadius: '8px',
                  borderColor: 'gray-line.main',
                }}
                src={allJobBoardData?.company_logo_url || companyDetails?.data?.organization_logo}
              />
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <Typography fontFamily={'Oswald'} fontWeight={600} variant={isMobile ? 'h2' : 'h1'}>
              {companyDetailsIsLoading ? (
                <Skeleton variant="text" width={'30%'} />
              ) : (
                allJobBoardData?.company_name || companyDetails?.data?.organization_name
              )}
            </Typography>
            <Typography variant="h4" sx={{ color: 'light-gray-text.main' }}>
              {companyDetailsIsLoading ? (
                <Skeleton variant="text" width={'30%'} />
              ) : (
                `${allJobBoardData?.state || companyDetails?.data?.state}, ${
                  allJobBoardData?.country || companyDetails?.data?.country
                }`
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Divider />
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
            Job Openings
          </Typography>
          <Box
            sx={{
              backgroundColor: allJobBoardIsLoading ? 'transparent' : 'alternate.light',
              borderRadius: '3.5px',
            }}
          >
            {allJobBoardIsLoading ? (
              <>
                <Skeleton variant="text" width={'120px'} />
              </>
            ) : !allJobBoardisError && !allJobBoardIsLoading ? (
              <Typography sx={{ color: 'alternate.main', padding: '8px 16px' }}>
                {`${totalJobs()} ${totalJobs() > 1 ? 'jobs' : 'job'} available`}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: isMobile ? '8px' : '16px',
            }}
          >
            <Box
              sx={{
                width: '100%',
              }}
            >
              <AdxInput
                disabled={jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError}
                placeholder="Search position"
                value={searchItem}
                onChange={(e) => handleSearch(e.target.value)}
                sxInput={{
                  height: '45px',
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    bgcolor: 'white.main',
                  },
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <Icon
                      sx={{
                        color:
                          jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError
                            ? 'gray-line.main'
                            : 'alternate.main',
                      }}
                    >
                      search
                    </Icon>
                  </InputAdornment>
                }
                endAdornment={
                  searchItem ? (
                    <InputAdornment position="end">
                      <Icon
                        onClick={handleClearSearch}
                        sx={{ color: 'error.main', cursor: 'pointer' }}
                      >
                        close
                      </Icon>
                    </InputAdornment>
                  ) : null
                }
              />
            </Box>
            {!isMobile && (
              <>
                <Box sx={{ width: '100%' }}>
                  <Select
                    disabled={jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError}
                    value={cityFilter?.value ?? {}}
                    // IconComponent={ExpandMoreIcon}
                    onChange={handleCityChange}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      height: '45px',
                      width: '100%',
                      '.MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                    renderValue={(params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {!cityFilter && (
                          <PlaceOutlinedIcon
                            sx={{
                              color:
                                jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError
                                  ? 'gray-line.main'
                                  : 'alternate.main',
                            }}
                          />
                        )}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: cityFilter ? 'dark-text.main' : 'light-gray-text.main',
                          }}
                        >
                          {cityFilter ?? 'All cities'}
                        </Typography>
                      </Box>
                    )}
                    IconComponent={
                      cityFilter === null
                        ? ExpandMoreIcon
                        : () => (
                            <IconButton onClick={() => setCityFilter(null)}>
                              <ClearIcon />
                            </IconButton>
                          )
                    }
                  >
                    {cityDataArray?.map((city, index) => (
                      <AdxMenuItem
                        key={index}
                        sx={{ margin: 1, borderRadius: '4.5px' }}
                        value={city?.value}
                        selectedvalue={cityFilter}
                      >
                        {city?.value}
                      </AdxMenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Select
                    disabled={jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError}
                    value={backgroundFilter ?? {}}
                    onChange={handleBackgroundChange}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      height: '45px',
                      width: '100%',
                      '.MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                    renderValue={(params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: backgroundFilter ? 'dark-text.main' : 'light-gray-text.main',
                          }}
                        >
                          {backgroundFilter?.job_background_name ?? 'All backgrounds'}
                        </Typography>
                      </Box>
                    )}
                    IconComponent={
                      backgroundFilter === null
                        ? ExpandMoreIcon
                        : () => (
                            <IconButton onClick={() => setBackgroundFilter(null)}>
                              <ClearIcon />
                            </IconButton>
                          )
                    }
                  >
                    {backgroundData?.map((background, index) => (
                      <AdxMenuItem
                        key={index}
                        sx={{ margin: 1, borderRadius: '4.5px' }}
                        value={background}
                        selectedvalue={backgroundFilter}
                      >
                        {background?.job_background_name}
                      </AdxMenuItem>
                    ))}
                  </Select>
                </Box>
                <AdxButton
                  variant={'contained'}
                  disabled={jobBoardStatus === JOB_BOARD_IS_INACTIVE || allJobBoardisError}
                  onClick={() => handleSearchJobsButton()}
                  sx={{ width: '100%', height: '45px', padding: '7px 16px', boxShadow: 'none' }}
                >
                  Search jobs
                </AdxButton>
              </>
            )}
            {isMobile && (
              <MobileJobBoardFilterDrawer
                backgroundData={backgroundData}
                backgroundFilter={backgroundFilter}
                setBackgroundFilter={setBackgroundFilter}
                cityDataArray={cityDataArray}
                cityFilter={cityFilter}
                setCityFilter={setCityFilter}
                handleCityChange={handleCityChange}
                handleBackgroundChange={handleBackgroundChange}
                allJobBoardisError={allJobBoardisError}
                typeFilter={typeFilter}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchJobsButton={handleSearchJobsButton}
                drawerState={drawerState}
                handleDrawer={handleDrawerState}
              />
            )}
          </Box>
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: '10px' }}>
              {typeFilter?.map((item, index) => (
                <AdxFormControlLabel
                  key={index}
                  label={item?.name}
                  labelPlacement="start"
                  sx={{
                    backgroundColor: 'gray-background.main',
                    paddingX: '8px',
                    color: 'light-gray-text.main',
                    borderRadius: '3.5px',
                    marginX: 0,
                  }}
                  control={
                    <AdxCheckbox
                      disabled={allJobBoardisError}
                      checked={item?.checked}
                      onChange={() => handleCheckboxChange(index)}
                    />
                  }
                />
              ))}
            </Box>
          )}
        </Box>
        {allJobBoardIsLoading ? (
          <JobBoardLoadingState />
        ) : allJobBoardisError ? (
          <JobBoardEmptyState />
        ) : allJobBoardData?.jobs === undefined && !allJobBoardIsLoading ? (
          <JobNotFoundState />
        ) : allJobBoardData?.jobs?.length === 0 ? (
          <JobNotFoundState />
        ) : (
          allJobBoardData?.jobs?.map((item, index) => {
            if (isMobile) {
              return (
                <>
                  <JobBoardMobileAccordion
                    item={item}
                    index={index}
                    isMobile={isMobile}
                    handleViewJob={handleViewJob}
                  />
                </>
              )
            } else {
              return (
                <Box key={index} sx={{ width: '100%', marginTop: '32px' }}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '24px',
                    }}
                  >
                    <Typography
                      fontFamily={'Oswald'}
                      fontWeight={600}
                      variant="h2"
                    >{`${item.job_background_name}`}</Typography>
                    <Box sx={{ backgroundColor: 'alternate.light', borderRadius: '3.5px' }}>
                      <Typography sx={{ color: 'alternate.main', padding: '8px 16px' }}>
                        {`${item.advertisements?.length} ${
                          item.advertisements?.length > 1 ? 'jobs' : 'job'
                        } available`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '24px',
                    }}
                  >
                    {item?.advertisements?.map((job, index) => {
                      const date = new Date(job?.publish_at)
                      const publishedDate = date?.toISOString().split('T')[0]

                      return (
                        <Box
                          key={index}
                          sx={{
                            padding: '24px 32px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: 1,
                            borderRadius: '4.5px',
                            borderColor: 'gray-line.main',
                            width: '100%',
                          }}
                        >
                          <Box sx={{ width: '100%' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: '#B8B8B8', marginBottom: '4px' }}
                            >
                              {`Posted on ${publishedDate}`}
                            </Typography>
                            <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
                              {job.position_title}
                            </Typography>
                            {!isMobile && (
                              <Typography variant="h4">{`${job.job_level_name} • RM ${job.min_salary} - RM ${job.max_salary}`}</Typography>
                            )}
                            {isMobile && (
                              <>
                                <Typography variant="h4">{`${job.job_level_name}`}</Typography>
                                <Typography>{`RM ${job.min_salary} - RM ${job.max_salary}`}</Typography>
                              </>
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginTop: '16px',
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: 'gray-background.main',
                                  borderRadius: '3.5px',
                                  border: 1,
                                  borderColor: 'gray-line.main',
                                  padding: '8px 16px',
                                }}
                              >
                                <Typography variant="subtitle2">
                                  {job.position_type === 'full-time'
                                    ? 'Full time'
                                    : job.position_type === 'part-time'
                                    ? 'Part time'
                                    : 'Contract'}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  backgroundColor: 'gray-background.main',
                                  borderRadius: '3.5px',
                                  border: 1,
                                  borderColor: 'gray-line.main',
                                  padding: '8px 16px',
                                }}
                              >
                                <PlaceOutlinedIcon sx={{ fontSize: '16px' }} />
                                <Typography variant="subtitle2">{job.city}</Typography>
                              </Box>
                            </Box>
                          </Box>
                          {!isMobile && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                              }}
                            >
                              <Typography
                                noWrap
                                variant="h4"
                                sx={{ color: 'alternate.main' }}
                                onClick={() => handleViewJob(job.id)}
                              >
                                View job
                              </Typography>
                              <Icon sx={{ color: 'alternate.main' }}>chevron_right</Icon>
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            }
          })
        )}
      </Box>
      <JobBoardFooter />
    </Box>
  )
}

export default JobBoard

export const JobNotFoundState = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        gap: '20px',
        paddingY: '100px',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'default-background.main',
          borderRadius: '100%',
          padding: '20px',
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
            borderRadius: '100%',
          }}
          src={notFoundIcon}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: '8px',
        }}
      >
        <Typography>No matching job listings were found.</Typography>
        <Typography
          sx={{ color: 'light-gray-text.main', textAlign: 'center' }}
          width={isMobile ? '100%' : '40%'}
        >
          Sorry, we couldn't find the job you're looking for. Try different keywords, check for
          typos, or widen your location and background filters.
        </Typography>
      </Box>
    </Box>
  )
}

export const JobBoardEmptyState = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        gap: '20px',
        paddingY: '100px',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'default-background.main',
          borderRadius: '100%',
          padding: '20px',
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
            borderRadius: '100%',
          }}
          src={notFoundIcon}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: '8px',
        }}
      >
        <Typography>This page doesn't exist.</Typography>
        <Typography sx={{ color: 'light-gray-text.main' }}>
          Sorry, we couldn't find the URL on our server.
        </Typography>
      </Box>
    </Box>
  )
}

export const JobBoardLoadingState = () => {
  return (
    <Box sx={{ width: '100%', marginTop: '32px' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
          <Skeleton variant="text" width={'150px'} />
        </Typography>
        <Box sx={{ borderRadius: '3.5px' }}>
          <Typography sx={{ color: 'alternate.main', padding: '8px 16px' }}>
            <Skeleton variant="text" width={'100px'} />
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            padding: '24px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: 1,
            borderRadius: '4.5px',
            borderColor: 'gray-line.main',
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#B8B8B8', marginBottom: '4px' }}>
              <Skeleton variant="text" />
            </Typography>
            <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
              <Skeleton variant="text" />
            </Typography>
            <Typography variant="h4">
              <Skeleton variant="text" />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Skeleton variant="rounded" width={'100px'} />
              <Skeleton variant="rounded" width={'100px'} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <Typography variant="h4" sx={{ color: 'alternate.main' }}>
              <Skeleton variant="text" width={'100px'} />
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
